import { i18n } from '@lingui/core'
import { isNil } from 'ramda'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { globalDefinitions } from '@/defined-translations'
import { ARRAY_TYPES, TEXT_TYPES } from '../enums'
import { hasMissingRequiredComment } from '../utils/questionnaire-evaluation.utils'

import type { IQuestionnaireQuestion, MultiSelectCommentAnswerItem } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters } from 'index'
import type { ObjectShape } from 'yup/lib/object'

function getValidationSchema(question: IQuestionnaireQuestion) {
  let questionValidationSchema

  if (TEXT_TYPES.has(question.type)) {
    questionValidationSchema = Yup.string()

    if (!isNil(question.maxlength)) {
      questionValidationSchema = questionValidationSchema.max(
        question.maxlength,
        i18n._(globalDefinitions.maxLength, { value: question.maxlength }),
      )
    }

    if (!isNil(question.minlength)) {
      questionValidationSchema = questionValidationSchema.min(
        question.minlength,
        i18n._(globalDefinitions.minLength, { value: question.minlength }),
      )
    }
  } else if (ARRAY_TYPES.has(question.type)) {
    questionValidationSchema =
      question.type === 'Array'
        ? (questionValidationSchema = Yup.array(
            Yup.object().shape(
              question.fields?.reduce((accumulator, currentValue) => {
                // Recursion for nested fields
                accumulator[currentValue.linkId] = getValidationSchema(currentValue as any)

                // Support for '_custom' value — reference the sibling field
                // within the same array item, not a hardcoded [0] index
                accumulator[`${currentValue.linkId}_custom`] = Yup.string().when(currentValue.linkId, {
                  is: '_custom',
                  then: Yup.string().required(),
                  otherwise: Yup.string().notRequired(),
                })

                return accumulator
              }, {} as ObjectShape) ?? {},
            ),
          ))
        : Yup.array()

    if (question.required) {
      questionValidationSchema = questionValidationSchema.min(1)
    }

    // MultiSelectComment: selected options with isCommentRequired must carry
    // a non-empty comment before the answer is valid
    if (question.type === 'MultiSelectComment') {
      questionValidationSchema = questionValidationSchema.test(
        'commentRequired',
        i18n._(globalDefinitions.commentRequired),
        (items) => !hasMissingRequiredComment(question, (items ?? []) as MultiSelectCommentAnswerItem[]),
      )
    }
  } else if (question.type === 'Number') {
    questionValidationSchema = Yup.number()

    if (!isNil(question.min)) {
      questionValidationSchema = questionValidationSchema.min(
        question.min,
        i18n._(globalDefinitions.minValue, { value: question.min }),
      )
    }

    if (!isNil(question.max)) {
      questionValidationSchema = questionValidationSchema.max(
        question.max,
        i18n._(globalDefinitions.maxValue, { value: question.max }),
      )
    }
  } else {
    questionValidationSchema = Yup.mixed()
  }

  questionValidationSchema = question.required
    ? questionValidationSchema.required(i18n._(globalDefinitions.required))
    : questionValidationSchema.notRequired()

  return questionValidationSchema
}

function useQuestionnaireValidationSchema(questionnaire: IQuestionnaireWithHealthParameters) {
  return useMemo(() => {
    const validationObject: { [key: string]: Yup.AnySchema } = {}

    questionnaire.item
      // Skip "Display" questions, as they can't be answered
      .filter((question) => question.type !== 'Display')
      .forEach((question) => {
        const questionId = question.linkId ?? question.id

        const questionValidationSchema = getValidationSchema(question)

        validationObject[questionId] = questionValidationSchema
      })

    return Yup.object(validationObject)
  }, [questionnaire.item])
}

export { useQuestionnaireValidationSchema }
